import { Injectable, inject } from "@angular/core";
import { WINDOW } from "../../features/authenticate/window-token";
import { environment } from "../../../environments/environment";

type UADataBrand = { brand: string; version: string };

type NavigatorWithUAData = Navigator & {
  userAgentData?: {
    brands: UADataBrand[];
    mobile: boolean;
  };
};

@Injectable({
  providedIn: "root"
})
export class BrowserSupportService {
  private readonly windowRef = inject(WINDOW);

  isSupportedBrowser(): boolean {
    if (!this.isRestrictionEnabled()) {
      return true;
    }

    // UA Client Hints is the only trusted source.
    // Chrome and Edge >= 89 always expose navigator.userAgentData. Since our
    // minimum supported version is 145, every legitimate Chrome or Edge will
    // have it present. Absent userAgentData means the browser is unsupported,
    // is masking its identity, or is below the supported floor — block it.
    const uaData = this.getUAClientHints();
    if (!uaData) {
      return false;
    }

    return this.isChromeBrandSupported(uaData) || this.isEdgeBrandSupported(uaData);
  }

  isRestrictionEnabled(): boolean {
    return environment.browserRestriction.enabled;
  }

  private getUAClientHints(): { brands: UADataBrand[] } | null {
    const nav = this.windowRef.navigator as NavigatorWithUAData;
    return nav.userAgentData ?? null;
  }

  private isChromeBrandSupported(uaData: { brands: UADataBrand[] }): boolean {
    const chromeBrand = uaData.brands.find((b) => b.brand === "Google Chrome");
    if (!chromeBrand) {
      return false;
    }

    const hasEdge = uaData.brands.some((b) => b.brand === "Microsoft Edge");
    if (hasEdge) {
      return false;
    }

    return parseInt(chromeBrand.version, 10) >= this.getMinimumChromeVersion();
  }

  private isEdgeBrandSupported(uaData: { brands: UADataBrand[] }): boolean {
    const edgeBrand = uaData.brands.find((b) => b.brand === "Microsoft Edge");
    if (!edgeBrand) {
      return false;
    }

    // Belt-and-suspenders: a real Edge never carries "Google Chrome" brand.
    // If both are present the UA data is malformed — block it.
    const hasChrome = uaData.brands.some((b) => b.brand === "Google Chrome");
    if (hasChrome) {
      return false;
    }

    return parseInt(edgeBrand.version, 10) >= this.getMinimumChromeVersion();
  }

  private getMinimumChromeVersion(): number {
    return environment.browserRestriction.minimumChromeVersion;
  }
}
